import React, { useEffect, useState } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { Link } from "react-router-dom";
import { toast } from "../../helpers/swal";
import { useNavigate } from "react-router-dom";
import { getDrivePrintFiles } from "../../api/drive-service";
import {
  uploadToServer,
  uploadToDrive,
  checkFile,
  deleteFile,
} from "../../api/drive-service";
import EmbroideryUploadModal from "../common/EmbroideryUploadModal";

// Pagination Component - EN ÜSTTE TANIMLA
const PaginationComponent = ({
  currentPage,
  totalPages,
  totalOrders,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div>
        <strong>Total Orders:</strong> {totalOrders} |<strong> Page:</strong>{" "}
        {currentPage} of {totalPages}
      </div>

      <nav>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
          </li>

          {currentPage > 3 && (
            <>
              <li className="page-item">
                <button
                  className="page-link"
                  onClick={() => handlePageChange(1)}
                >
                  1
                </button>
              </li>
              {currentPage > 4 && (
                <li className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              )}
            </>
          )}

          {getPageNumbers().map((page) => (
            <li
              key={page}
              className={`page-item ${currentPage === page ? "active" : ""}`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            </li>
          ))}

          {currentPage < totalPages - 2 && (
            <>
              {currentPage < totalPages - 3 && (
                <li className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              )}
              <li className="page-item">
                <button
                  className="page-link"
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              </li>
            </>
          )}

          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

const getRequestedServiceStyle = (service) => {
  const s = service?.toLowerCase() || "";
  if (s.includes("next day")) return { backgroundColor: "#ffcccc" };
  if (s.includes("priority")) return { backgroundColor: "#ffe0b3" };
  if (s.includes("standard")) return { backgroundColor: "#d4edda" };
  if (s.includes("local")) return { backgroundColor: "#d1ecf1" };
  return {};
};

const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj) || "";
};

const getSortIcon = (key, currentKey, order) => {
  if (currentKey !== key) return "⇅";
  return order === "asc" ? "⬆️" : "⬇️";
};

const isOrderComplete = (order) => {
  if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
    return false;
  }

  const validItems = order.items.filter(
    (item) => !["discount", "shipping"].includes(item.name?.toLowerCase() || "")
  );

  if (validItems.length === 0) {
    return false;
  }

  const firstItem = validItems[0];

  if (!firstItem.name || !firstItem.quantity) {
    return false;
  }

  return true;
};

const MyOrdersPage = () => {
  const [driveFolders, setDriveFolders] = useState([]);
  const [embroideryFolderId, setEmbroideryFolderId] = useState(null);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(() => {
    const savedShop = localStorage.getItem("selectedShop");
    return savedShop ? JSON.parse(savedShop) : { id: "", name: "" };
  });
  const [statusFilter, setStatusFilter] = useState("awaiting_shipment");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [tags, setTags] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [showEmbModal, setShowEmbModal] = useState(false);
  const [selectedEmbOrder, setSelectedEmbOrder] = useState(null);
  const embroideryTag = tags.find((tag) => tag.name === "7-Embroidery");
  const embroideryTagId = embroideryTag ? Number(embroideryTag.tagId) : null;
  const navigate = useNavigate();
  const [tagUpdating, setTagUpdating] = useState(false);
  const [selectedTagFilter, setSelectedTagFilter] = useState("all");
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);

  // Yeni Pagination State'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [itemsPerPage] = useState(50);

  // Internal Note Filter States
  const [selectedNoteFilters, setSelectedNoteFilters] = useState(["all"]);
  const [isNoteDropdownOpen, setIsNoteDropdownOpen] = useState(false);
  const [availableNotes, setAvailableNotes] = useState([]);

  // Yeni eklenen state'ler
  const [selectedSkuFilter, setSelectedSkuFilter] = useState("all");
  const [availableSkus, setAvailableSkus] = useState([]);
  const [bulkNote, setBulkNote] = useState("");
  const [savingBulkNotes, setSavingBulkNotes] = useState(false);

  // Tag loading state
  const [tagsLoading, setTagsLoading] = useState(false);

  // selectedShop değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (selectedShop.id) {
      localStorage.setItem("selectedShop", JSON.stringify(selectedShop));
    }
  }, [selectedShop]);

  useEffect(() => {
    axios
      .get(`${settings.apiURL}/get_user_shops.php`, { headers: authHeader() })
      .then((res) => {
        setShops(res.data.shops);

        if (res.data.shops.length > 0) {
          // Önce localStorage'dan kayıtlı shop'u kontrol et
          const savedShop = localStorage.getItem("selectedShop");
          if (savedShop) {
            const parsedShop = JSON.parse(savedShop);
            setSelectedShop(parsedShop);
            fetchOrders(parsedShop.id, parsedShop.name, statusFilter, 1);
          } else {
            // Yoksa eski mantıkla devam et
            const savedShopName = localStorage.getItem("selectedShopName");
            const matchedShop = res.data.shops.find(
              (shop) => shop.shop_name === savedShopName
            );

            if (matchedShop) {
              const newShop = {
                id: matchedShop.shipstation_store_id,
                name: matchedShop.shop_name,
              };
              setSelectedShop(newShop);
              fetchOrders(
                matchedShop.shipstation_store_id,
                matchedShop.shop_name,
                statusFilter,
                1
              );
            } else {
              setSelectedShop({ id: "all", name: "All Shops" });
              fetchOrders("all", "All Shops", statusFilter, 1);
            }
          }
        }
      })
      .catch((err) => console.error("Error fetching shops:", err));
  }, []);

  useEffect(() => {
    const savedSortKey = localStorage.getItem("myOrdersSortKey");
    const savedSortOrder = localStorage.getItem("myOrdersSortOrder");

    if (savedSortKey) setSortKey(savedSortKey);
    if (savedSortOrder) setSortOrder(savedSortOrder);
  }, []);

  // Fetch tags once (with loading state) to avoid duplicate calls
  useEffect(() => {
    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        const res = await axios.get(
          `${settings.apiURL}/get_shipstation_tags.php`,
          {
            headers: authHeader(),
          }
        );
        if (res.data?.tags) {
          setTags(res.data.tags);
        } else {
          setTags([]);
        }
      } catch (err) {
        console.error("Error fetching tags:", err);
        setTags([]);
      } finally {
        setTagsLoading(false);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        setCurrentPage(1);
        fetchOrders(selectedShop.id, selectedShop.name, statusFilter, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, statusFilter, selectedShop]);

  useEffect(() => {
    const updatedDrafts = orders.reduce((acc, order) => {
      acc[order.orderId] = order.internalNotes || "";
      return acc;
    }, {});
    setNoteDrafts(updatedDrafts);
  }, [orders]);

  // SessionStorage'dan note filterlarını oku
  useEffect(() => {
    const savedNoteFilters = sessionStorage.getItem(
      `noteFilters_${selectedShop?.id}`
    );
    if (savedNoteFilters) {
      try {
        const parsed = JSON.parse(savedNoteFilters);
        setSelectedNoteFilters(parsed);
      } catch (e) {
        setSelectedNoteFilters(["all"]);
      }
    } else {
      setSelectedNoteFilters(["all"]);
    }
  }, [selectedShop?.id]);

  // SessionStorage'dan tag filterını oku
  useEffect(() => {
    const savedTagFilter = sessionStorage.getItem(
      `tagFilter_${selectedShop?.id}`
    );
    if (savedTagFilter) {
      setSelectedTagFilter(savedTagFilter);
    } else {
      setSelectedTagFilter("all");
    }
  }, [selectedShop?.id]);

  // SessionStorage'dan SKU filterını oku
  useEffect(() => {
    const savedSkuFilter = sessionStorage.getItem(
      `skuFilter_${selectedShop?.id}`
    );
    if (savedSkuFilter) {
      setSelectedSkuFilter(savedSkuFilter);
    } else {
      setSelectedSkuFilter("all");
    }
  }, [selectedShop?.id]);

  // Siparişler değiştiğinde available notes ve SKU'ları güncelle
  useEffect(() => {
    if (orders.length > 0) {
      const uniqueNotes = new Set();
      const uniqueSkus = new Set();

      orders.forEach((order) => {
        if (order.internalNotes && order.internalNotes.trim()) {
          uniqueNotes.add(order.internalNotes.trim());
        }

        if (order.items && Array.isArray(order.items)) {
          order.items.forEach((item) => {
            if (item.sku && item.sku.trim()) {
              uniqueSkus.add(item.sku.trim());
            }
          });
        }
      });
      setAvailableNotes(Array.from(uniqueNotes).sort());
      setAvailableSkus(Array.from(uniqueSkus).sort());
    } else {
      setAvailableNotes([]);
      setAvailableSkus([]);
    }
  }, [orders]);

  // Note filter değiştiğinde sessionStorage'a kaydet
  useEffect(() => {
    if (selectedShop?.id) {
      sessionStorage.setItem(
        `noteFilters_${selectedShop.id}`,
        JSON.stringify(selectedNoteFilters)
      );
    }
  }, [selectedNoteFilters, selectedShop?.id]);

  // Tag filter değiştiğinde sessionStorage'a kaydet
  useEffect(() => {
    if (selectedShop?.id) {
      sessionStorage.setItem(`tagFilter_${selectedShop.id}`, selectedTagFilter);
    }
  }, [selectedTagFilter, selectedShop?.id]);

  // SKU filter değiştiğinde sessionStorage'a kaydet
  useEffect(() => {
    if (selectedShop?.id) {
      sessionStorage.setItem(`skuFilter_${selectedShop.id}`, selectedSkuFilter);
    }
  }, [selectedSkuFilter, selectedShop?.id]);

  const handleNoteChange = (orderId, value) => {
    setNoteDrafts((prev) => ({
      ...prev,
      [orderId]: value,
    }));
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleTagChange = (orderId, selectedTagId) => {
    setTagUpdating(true);

    if (selectedOrders.includes(orderId) && selectedOrders.length > 1) {
      const existingTagIds = {};
      selectedOrders.forEach((id) => {
        const currentTag =
          orders.find((o) => o.orderId === id)?.tagIds?.[0] || null;
        existingTagIds[id] = currentTag ? [currentTag] : [];
      });

      axios
        .post(
          `${settings.apiURL}/update_multiple_order_tags.php`,
          {
            orderIds: selectedOrders,
            tagId: selectedTagId,
            existingTagIds: existingTagIds,
          },
          { headers: authHeader() }
        )
        .then((res) => {
          toast(
            res.data.message || "Tags updated",
            res.data.success ? "success" : "error"
          );

          if (res.data.success) {
            setOrders((prevOrders) =>
              prevOrders.map((order) =>
                selectedOrders.includes(order.orderId)
                  ? {
                      ...order,
                      tagIds: selectedTagId === "remove" ? [] : [selectedTagId],
                    }
                  : order
              )
            );
            setSelectedOrders([]);
            setSelectedTag("");
          }
        })
        .catch(() => toast("Something went wrong", "error"))
        .finally(() => {
          setTagUpdating(false);
        });
    } else {
      const currentTag =
        orders.find((o) => o.orderId === orderId)?.tagIds?.[0] || null;

      const payload =
        selectedTagId === "remove"
          ? {
              orderNumber: orders.find((o) => o.orderId === orderId)
                ?.orderNumber,
              orderId,
              tagId: "remove",
              removeTagId: currentTag,
            }
          : {
              orderNumber: orders.find((o) => o.orderId === orderId)
                ?.orderNumber,
              orderId,
              tagId: selectedTagId,
            };

      axios
        .post(`${settings.apiURL}/update_order_tag.php`, payload, {
          headers: authHeader(),
        })
        .then((res) => {
          if (res.data.success) {
            toast(res.data.message || "Tag updated", "success");

            setOrders((prevOrders) =>
              prevOrders.map((o) =>
                o.orderId === orderId
                  ? {
                      ...o,
                      tagIds:
                        selectedTagId === "remove"
                          ? []
                          : [Number(selectedTagId)],
                    }
                  : o
              )
            );
            setSelectedOrders([]);
            setSelectedTag("");
          } else {
            toast("Failed to update tag!", "error");
          }
        })
        .catch((err) => {
          console.error(err);
          toast("Tag update failed!", "error");
        })
        .finally(() => {
          setTagUpdating(false);
        });
    }
  };

  const fetchOrders = (
    shopId = selectedShop?.id,
    shopName = selectedShop?.name,
    status = statusFilter,
    page = currentPage,
    limit = itemsPerPage
  ) => {
    if (!shopId) {
      alert("Please select a shop!");
      return;
    }

    setLoading(true);
    setOrders([]);

    // "shipped" ve "cancelled" için yeni pagination endpoint kullan
    if (status === "shipped" || status === "cancelled") {
      let url = `${settings.apiURL}/get_orders_by_status_paginated.php`;
      url += `?store_id=${shopId}&status=${status}&page=${page}&limit=${limit}`;

      if (searchQuery.trim() !== "") {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      axios
        .get(url, { headers: authHeader() })
        .then((res) => {
          if (
            res.data.success &&
            res.data.orders &&
            Array.isArray(res.data.orders)
          ) {
            // Data JSON'ını parse et ve order objelerini birleştir
            const parsedOrders = res.data.orders.map((order) => {
              try {
                const dataObj = JSON.parse(order.data);
                return {
                  ...order,
                  ...dataObj, // data'daki tüm property'leri order'a kopyala
                  orderId: order.order_id || dataObj.orderId, // orderId'yi standartlaştır
                  orderNumber: order.order_number || dataObj.orderNumber, // orderNumber'ı standartlaştır
                  shopName:
                    dataObj.shopName ||
                    dataObj.advancedOptions?.storeId ||
                    shopName,
                };
              } catch (error) {
                console.error(
                  "JSON parse error for order:",
                  order.order_id,
                  error
                );
                return order;
              }
            });

            const completeOrders = parsedOrders.filter(isOrderComplete);
            setOrders(completeOrders);

            // Pagination bilgilerini set et
            if (res.data.pagination) {
              setCurrentPage(res.data.pagination.current_page);
              setTotalPages(res.data.pagination.total_pages);
              setTotalOrdersCount(res.data.pagination.total_orders);
            }
          } else {
            setOrders([]);
            setCurrentPage(1);
            setTotalPages(1);
            setTotalOrdersCount(0);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching orders:", err);
          setLoading(false);
          setOrders([]);
          setCurrentPage(1);
          setTotalPages(1);
          setTotalOrdersCount(0);
        });
    } else {
      // Diğer status'ler için mevcut logic (awaiting_shipment, on_hold)
      let url = `${settings.apiURL}/get_user_orders_from_db.php`;

      if (shopId === "all") {
        url += `?store_id=all`;
      } else {
        url += `?store_id=${shopId}`;
      }

      if (status !== "awaiting_shipment") {
        url = `${settings.apiURL}/get_orders_by_status.php`;
        url += `?store_id=${shopId}&order_status=${status}`;
      }

      if (searchQuery.trim() !== "") {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      axios
        .get(url, { headers: authHeader() })
        .then((res) => {
          if (res.data.orders && Array.isArray(res.data.orders)) {
            // Mevcut endpoint'ler için de data parse işlemi
            const parsedOrders = res.data.orders.map((order) => {
              if (order.data && typeof order.data === "string") {
                try {
                  const dataObj = JSON.parse(order.data);
                  return {
                    ...order,
                    ...dataObj,
                    orderId: order.order_id || dataObj.orderId,
                    orderNumber: order.order_number || dataObj.orderNumber,
                  };
                } catch (error) {
                  console.error(
                    "JSON parse error for order:",
                    order.order_id,
                    error
                  );
                  return order;
                }
              }
              return order;
            });

            const completeOrders = parsedOrders.filter(isOrderComplete);
            setOrders(completeOrders);
            // Mevcut endpoint'ler pagination dönmüyor
            setCurrentPage(1);
            setTotalPages(1);
            setTotalOrdersCount(completeOrders.length);
          } else {
            setOrders([]);
            setCurrentPage(1);
            setTotalPages(1);
            setTotalOrdersCount(0);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching orders:", err);
          setLoading(false);
          setOrders([]);
          setCurrentPage(1);
          setTotalPages(1);
          setTotalOrdersCount(0);
        });
    }
  };

  const calculateAge = (orderDate) => {
    const now = new Date();
    const texasOffset = -5;
    const orderTime = new Date(orderDate);
    const diffInHours = Math.floor(
      (now.getTime() + texasOffset * 60 * 60 * 1000 - orderTime.getTime()) /
        (1000 * 60 * 60)
    );
    return diffInHours;
  };

  const getAgeCellStyle = (hours) => {
    if (hours < 24) {
      return { backgroundColor: "#d4edda", color: "#155724" };
    } else if (hours < 48) {
      return { backgroundColor: "#fff3cd", color: "#856404" };
    } else {
      return { backgroundColor: "#f8d7da", color: "#721c24" };
    }
  };

  const handleSort = (key) => {
    let newOrder = "asc";
    if (sortKey === key) {
      newOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortKey(key);
    setSortOrder(newOrder);

    localStorage.setItem("myOrdersSortKey", key);
    localStorage.setItem("myOrdersSortOrder", newOrder);
  };

  const openEmbroideryModal = (order) => {
    setSelectedEmbOrder(order);
    setShowEmbModal(true);
    // Modal kendi içinde google token bekleme / spinner mantığını uyguluyor.
  };

  const handleEmbroideryUpload = (order) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".png";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const user_id = localStorage.getItem("user_id");
        const tempResp = await uploadToServer(
          user_id,
          "EMBROIDERY",
          file,
          "EMBROIDERY"
        );

        const temp_id = tempResp.data.response.temp_id;
        const temp_path = tempResp.data.response.temp_path;

        const driveResp = await uploadToDrive(
          temp_id,
          temp_path,
          embroideryFolderId,
          "EMBROIDERY"
        );

        const fileId = driveResp.data.file_id;

        const checkResp = await checkFile(temp_id, fileId, temp_path);

        toast(`${file.name} uploaded to EMBROIDERY`, "success");
      } catch (error) {
        console.error("Upload error:", error);
        toast(`Error uploading file: ${error.message}`, "error");
      }
    };

    input.click();
  };

  // Toplu not kaydetme fonksiyonu
  const handleBulkNoteSave = async () => {
    if (selectedOrders.length === 0) {
      toast("Please select at least one order", "warning");
      return;
    }

    if (!bulkNote.trim()) {
      toast("Please enter a note", "warning");
      return;
    }

    setSavingBulkNotes(true);

    try {
      const batchSize = 5;
      const batches = [];

      for (let i = 0; i < selectedOrders.length; i += batchSize) {
        batches.push(selectedOrders.slice(i, i + batchSize));
      }

      let successCount = 0;
      let errorCount = 0;

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        const batchPromises = batch.map((orderId) =>
          axios
            .post(
              `${settings.apiURL}/update_order_notes.php`,
              {
                orderId,
                internalNote: bulkNote.trim(),
              },
              { headers: authHeader() }
            )
            .then((res) => ({ success: true, orderId, data: res.data }))
            .catch((err) => ({ success: false, orderId, error: err }))
        );

        const batchResults = await Promise.allSettled(batchPromises);

        batchResults.forEach((result) => {
          if (result.status === "fulfilled" && result.value.success) {
            successCount++;
            setOrders((prev) =>
              prev.map((order) =>
                order.orderId === result.value.orderId
                  ? { ...order, internalNotes: bulkNote.trim() }
                  : order
              )
            );
            setNoteDrafts((prev) => ({
              ...prev,
              [result.value.orderId]: bulkNote.trim(),
            }));
          } else {
            errorCount++;
          }
        });

        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      if (errorCount === 0) {
        toast(
          `Successfully updated notes for ${successCount} orders`,
          "success"
        );
        setBulkNote("");
        setSelectedOrders([]);
      } else {
        toast(
          `Updated ${successCount} orders, ${errorCount} failed`,
          "warning"
        );
      }
    } catch (error) {
      console.error("Bulk note update error:", error);
      toast("Error updating bulk notes", "error");
    } finally {
      setSavingBulkNotes(false);
    }
  };

  const handleStatusChange = (e) => {
    const selectedStatus = e.target.value;
    setStatusFilter(selectedStatus);
    setCurrentPage(1);
    if (!loading) {
      fetchOrders(selectedShop.id, selectedShop.name, selectedStatus, 1);
    }
  };

  const handleNoteFilterChange = (noteValue) => {
    if (noteValue === "all") {
      setSelectedNoteFilters(["all"]);
    } else {
      const newFilters = selectedNoteFilters.includes("all")
        ? [noteValue]
        : selectedNoteFilters.includes(noteValue)
        ? selectedNoteFilters.filter((f) => f !== noteValue)
        : [...selectedNoteFilters.filter((f) => f !== "all"), noteValue];

      if (newFilters.length === 0) {
        setSelectedNoteFilters(["all"]);
      } else {
        setSelectedNoteFilters(newFilters);
      }
    }
  };

  const truncateNote = (note, maxLength = 25) => {
    if (!note) return "";
    return note.length > maxLength
      ? note.substring(0, maxLength) + "..."
      : note;
  };

  const sortedOrders = [...orders].sort((a, b) => {
    if (!sortKey) return 0;

    const getVal = (obj, key) => {
      if (key === "quantity") {
        const filtered =
          obj.items?.filter(
            (item) =>
              !["discount", "shipping"].includes(item.name.toLowerCase())
          ) || [];
        return filtered.reduce((sum, item) => sum + (item.quantity || 0), 0);
      }

      if (key === "age") {
        return calculateAge(obj.createDate);
      }

      if (key === "designStatus") {
        if (obj.isPlaced) return 2;
        if (obj.hasDesign) return 1;
        return 0;
      }

      return getNestedValue(obj, key)?.toString().toLowerCase() || "";
    };

    const valA = getVal(a, sortKey);
    const valB = getVal(b, sortKey);

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;

    const skuA = getNestedValue(a, "items.0.sku").toLowerCase();
    const skuB = getNestedValue(b, "items.0.sku").toLowerCase();
    return skuA.localeCompare(skuB);
  });

  const filteredOrders = sortedOrders.filter((order) => {
    const matchesSearch =
      order.orderNumber?.toString().includes(searchQuery) ||
      order.shipTo?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTag =
      selectedTagFilter === "all"
        ? true
        : selectedTagFilter === "notag"
        ? !Array.isArray(order.tagIds) || order.tagIds.length === 0
        : Array.isArray(order.tagIds) &&
          order.tagIds.includes(Number(selectedTagFilter));

    const matchesNoteFilter = (() => {
      if (selectedNoteFilters.includes("all")) return true;

      const orderNote = order.internalNotes?.trim() || "";

      if (selectedNoteFilters.includes("empty") && !orderNote) return true;

      return selectedNoteFilters.some(
        (filter) =>
          filter !== "all" && filter !== "empty" && orderNote === filter
      );
    })();

    const matchesSku = (() => {
      if (selectedSkuFilter === "all") return true;

      if (!order.items || !Array.isArray(order.items)) return false;

      return order.items.some(
        (item) => item.sku && item.sku.trim() === selectedSkuFilter
      );
    })();

    return matchesSearch && matchesTag && matchesNoteFilter && matchesSku;
  });

  const showEmbroideryColumn = filteredOrders.some((order) =>
    order?.tagIds?.map(Number).includes(embroideryTagId)
  );

  const hasEmbroideryTag = filteredOrders.some((order) =>
    tags.find(
      (tag) => tag.name === "7-Embroidery" && order.tagIds?.includes(tag.tagId)
    )
  );

  const handleNoteSave = (orderId) => {
    const note = noteDrafts[orderId]?.trim() || "";

    setSavingNoteId(orderId);

    const noteToSave = note === "" ? null : note;

    axios
      .post(
        `${settings.apiURL}/update_order_notes.php`,
        { orderId, internalNote: noteToSave },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast("Internal note updated", "success");
          setOrders((prev) =>
            prev.map((order) =>
              order.orderId === orderId
                ? { ...order, internalNotes: noteToSave }
                : order
            )
          );
        } else {
          toast("Failed to update note", "error");
        }
      })
      .catch(() => {
        toast("Error updating note", "error");
      })
      .finally(() => {
        setSavingNoteId(null);
      });
  };

  return (
    <div className="container-fluid p-4" style={{ fontSize: "13px" }}>
      <div className="text-center mb-4">
        <h2>My Orders</h2>
        <Link to="/home" className="btn btn-outline-primary mt-2">
          🏠 Home
        </Link>
      </div>

      <div className="card mb-3">
        <div className="card-header bg-primary text-white">
          <strong>📝 Bulk Note </strong>
        </div>
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-6">
              <textarea
                className="form-control"
                placeholder="Enter note to add to all selected orders..."
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                rows="2"
                style={{ fontSize: "13px" }}
              />
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <span className="me-2">
                  <strong>Selected:</strong> {selectedOrders.length} order
                </span>
                {selectedOrders.length > 0 && (
                  <button
                    className="btn btn-outline-danger btn-sm ms-2"
                    onClick={() => setSelectedOrders([])}
                    title="Clear selected"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-success w-100"
                onClick={handleBulkNoteSave}
                disabled={
                  selectedOrders.length === 0 ||
                  !bulkNote.trim() ||
                  savingBulkNotes
                }
              >
                {savingBulkNotes ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Kaydediliyor...
                  </>
                ) : (
                  `Save Note (${selectedOrders.length})`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-2" style={{ fontSize: "14px" }}>
        <strong>Total Orders:</strong> {filteredOrders.length} |{" "}
        <strong>Orders with Tags:</strong>{" "}
        {
          filteredOrders.filter(
            (order) => order.tagIds && order.tagIds.length > 0
          ).length
        }
      </div>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <select
          className="form-select form-select-sm w-auto"
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
          disabled={tagUpdating}
        >
          <option value="">Select Tag</option>
          {tags.map((tag) => (
            <option key={tag.tagId} value={tag.tagId}>
              {tag.name}
            </option>
          ))}
          <option value="remove">Remove Tag</option>
        </select>

        <button
          className="btn btn-sm btn-success d-flex align-items-center"
          style={{ padding: "4px 10px", fontSize: "12px" }}
          disabled={selectedOrders.length === 0 || !selectedTag || tagUpdating}
          onClick={() => {
            if (!selectedTag) {
              toast("Please select a tag", "warning");
              return;
            }
            if (selectedOrders.length === 0) {
              toast("No orders selected", "warning");
              return;
            }

            setTagUpdating(true);
            handleTagChange(selectedOrders[0], selectedTag);
          }}
        >
          {tagUpdating && (
            <span
              className="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
          )}
          {tagUpdating ? "Applying..." : `Apply Tag (${selectedOrders.length})`}
        </button>

        <select
          className="form-select w-auto"
          value={statusFilter}
          onChange={handleStatusChange}
        >
          {selectedShop.id === "all" ? (
            <option value="awaiting_shipment">Awaiting Shipment</option>
          ) : (
            <>
              <option value="awaiting_shipment">Awaiting Shipment</option>
              <option value="shipped">Shipped</option>
              <option value="cancelled">Cancelled</option>
              <option value="on_hold">On Hold</option>
            </>
          )}
        </select>

        <input
          className="form-control w-auto"
          type="text"
          placeholder="Search by Order # or Customer"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <button
          className="btn btn-primary"
          onClick={() => {
            setCurrentPage(1);
            fetchOrders(selectedShop.id, selectedShop.name, statusFilter, 1);
          }}
        >
          Fetch Orders
        </button>

        <button
          className="btn btn-outline-secondary"
          onClick={() => {
            if (!searchQuery.trim()) {
              toast("Please enter a search term", "warning");
              return;
            }

            axios
              .get(
                `${
                  settings.apiURL
                }/special_search_order.php?query=${encodeURIComponent(
                  searchQuery
                )}`,
                {
                  headers: authHeader(),
                }
              )
              .then((res) => {
                if (res.data.success && res.data.orders.length > 0) {
                  const firstOrder = res.data.orders[0];
                  navigate(`/order-details/${firstOrder.orderId}`, {
                    state: { orders: [firstOrder] },
                  });
                } else {
                  toast("No matching orders found", "info");
                }
              })
              .catch((err) => {
                console.error("Search error:", err);
                toast("Search failed", "error");
              });
          }}
        >
          🔍 Search
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: "700px" }}
        >
          <table className="table table-bordered table-hover table-sm">
            <thead
              className="table-light"
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 10,
              }}
            >
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      filteredOrders.length > 0 &&
                      selectedOrders.length === filteredOrders.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allIds = filteredOrders.map(
                          (order) => order.orderId
                        );
                        setSelectedOrders(allIds);
                      } else {
                        setSelectedOrders([]);
                      }
                    }}
                  />
                </th>

                <th>#</th>
                <th
                  onClick={() => handleSort("orderNumber")}
                  style={{
                    cursor: "pointer",
                    userSelect: "none",
                    minWidth: "120px",
                    width: "120px",
                  }}
                >
                  Order # {getSortIcon("orderNumber", sortKey, sortOrder)}
                </th>
                <th
                  onClick={() => handleSort("age")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Age {getSortIcon("age", sortKey, sortOrder)}
                </th>
                <th
                  onClick={() => handleSort("shipTo.name")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Recipient {getSortIcon("shipTo.name", sortKey, sortOrder)}
                </th>
                <th style={{ minWidth: "100px", width: "100px" }}>
                  {" "}
                  {/* ESKİ: daha genişti */}
                  Shop
                  <select
                    className="form-select form-select-sm"
                    value={selectedShop.id}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const shopName =
                        shops.find(
                          (shop) => shop.shipstation_store_id === selectedValue
                        )?.shop_name || "All Shops";

                      const newShop = {
                        id: selectedValue,
                        name: shopName,
                      };

                      setSelectedShop(newShop);
                      setCurrentPage(1);
                      fetchOrders(selectedValue, shopName, statusFilter, 1);
                    }}
                    style={{ display: "inline", width: "150px" }}
                  >
                    <option value="all">All Shops</option>
                    {shops.map((shop) => (
                      <option
                        key={shop.shipstation_store_id}
                        value={shop.shipstation_store_id}
                      >
                        {shop.shop_name}
                      </option>
                    ))}
                  </select>
                </th>

                <th>
                  Note
                  <select
                    value={
                      selectedNoteFilters.includes("all")
                        ? "all"
                        : selectedNoteFilters.length === 1
                        ? selectedNoteFilters[0]
                        : "multiple"
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "all") {
                        setSelectedNoteFilters(["all"]);
                      } else if (value === "empty") {
                        setSelectedNoteFilters(["empty"]);
                      } else {
                        setSelectedNoteFilters([value]);
                      }
                    }}
                    style={{ marginLeft: "6px" }}
                  >
                    <option value="all">All Notes</option>
                    <option value="empty">Empty Notes</option>
                    {availableNotes.map((note, index) => (
                      <option key={index} value={note}>
                        {truncateNote(note, 20)}
                      </option>
                    ))}
                    {selectedNoteFilters.length > 1 &&
                      !selectedNoteFilters.includes("all") && (
                        <option value="multiple">Multiple Selected</option>
                      )}
                  </select>
                </th>

                <th style={{ minWidth: "120px", width: "120px" }}>
                  Tags
                  <select
                    value={selectedTagFilter}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedTagFilter(value);

                      const matchesSearch = (order) =>
                        order.orderNumber?.toString().includes(searchQuery) ||
                        order.shipTo?.name
                          ?.toLowerCase()
                          .includes(searchQuery.toLowerCase());

                      const matchesTagWith = (order, tagValue) => {
                        if (tagValue === "all") return true;
                        if (tagValue === "notag")
                          return (
                            !Array.isArray(order.tagIds) ||
                            order.tagIds.length === 0
                          );
                        return (
                          Array.isArray(order.tagIds) &&
                          order.tagIds.includes(Number(tagValue))
                        );
                      };

                      const hasAny = sortedOrders.some(
                        (o) => matchesSearch(o) && matchesTagWith(o, value)
                      );

                      if (value !== "all" && !hasAny) {
                        toast(
                          "No orders for the selected tag. Showing all orders.",
                          "info"
                        );
                        setSelectedTagFilter("all");
                      }
                    }}
                    style={{ marginLeft: "6px" }}
                  >
                    <option value="all">All</option>
                    <option value="notag">No tag</option>
                    {tags.map((tag) => (
                      <option key={tag.tagId} value={String(tag.tagId)}>
                        {tag.name}
                      </option>
                    ))}
                  </select>
                </th>

                <th>
                  SKU
                  <select
                    value={selectedSkuFilter}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedSkuFilter(value);

                      const matchesSearch = (order) =>
                        order.orderNumber?.toString().includes(searchQuery) ||
                        order.shipTo?.name
                          ?.toLowerCase()
                          .includes(searchQuery.toLowerCase());

                      const matchesSkuWith = (order, skuValue) => {
                        if (skuValue === "all") return true;
                        if (!order.items || !Array.isArray(order.items))
                          return false;
                        return order.items.some(
                          (item) => item.sku && item.sku.trim() === skuValue
                        );
                      };

                      const hasAny = sortedOrders.some(
                        (o) => matchesSearch(o) && matchesSkuWith(o, value)
                      );

                      if (value !== "all" && !hasAny) {
                        toast(
                          "No orders for the selected SKU. Showing all orders.",
                          "info"
                        );
                        setSelectedSkuFilter("all");
                      }
                    }}
                    style={{ marginLeft: "6px" }}
                  >
                    <option value="all">All SKUs</option>
                    {availableSkus.map((sku, index) => (
                      <option key={index} value={sku}>
                        {sku.length > 15 ? sku.substring(0, 15) + "..." : sku}
                      </option>
                    ))}
                  </select>
                </th>

                <th
                  onClick={() => handleSort("quantity")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Quantity {getSortIcon("quantity", sortKey, sortOrder)}
                </th>
                <th>Gift</th>
                <th
                  onClick={() => handleSort("items.0.name")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Item Name {getSortIcon("items.0.name", sortKey, sortOrder)}
                </th>
                <th>Requested Service</th>
                <th
                  onClick={() => handleSort("createDate")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Order Date {getSortIcon("createDate", sortKey, sortOrder)}
                </th>
                <th
                  onClick={() => handleSort("items.0.sku")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Item SKU {getSortIcon("items.0.sku", sortKey, sortOrder)}
                </th>
                <th
                  onClick={() => handleSort("orderStatus")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Status {getSortIcon("orderStatus", sortKey, sortOrder)}
                </th>
                {showEmbroideryColumn && <th>Edit</th>}

                <th
                  style={{
                    cursor: "pointer",
                    minWidth: "100px",
                    width: "100px",
                  }}
                  onClick={() => handleSort("designStatus")}
                >
                  Design Status{" "}
                  {sortKey === "designStatus"
                    ? sortOrder === "asc"
                      ? "↑"
                      : "↓"
                    : ""}
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((order, index) => {
                const itemsFiltered =
                  order.items?.filter(
                    (item) =>
                      !["discount", "shipping"].includes(
                        item.name.toLowerCase()
                      )
                  ) || [];
                const totalQuantity = itemsFiltered.reduce(
                  (sum, item) => sum + (item.quantity || 0),
                  0
                );
                const firstItem = itemsFiltered[0] || {};
                const ageHours = calculateAge(order.createDate);

                return (
                  <tr key={order.orderId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.orderId)}
                        onChange={() => handleCheckboxChange(order.orderId)}
                      />
                    </td>
                    <td>{index + 1}</td>
                    <td
                      style={{
                        minWidth: "120px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={order.orderNumber}
                    >
                      <Link
                        to={`/order-details/${order.orderId}`}
                        state={{ orders: filteredOrders }}
                        style={{ color: "blue", textDecoration: "underline" }}
                      >
                        {order.orderNumber}
                      </Link>
                    </td>
                    <td
                      style={{
                        ...getAgeCellStyle(ageHours),
                        maxWidth: "20px",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {ageHours} hr
                    </td>
                    <td
                      style={{
                        maxWidth: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={order.shipTo?.name || "-"}
                    >
                      {order.shipTo?.name || "-"}
                    </td>
                    <td>{order.shopName || "-"}</td>

                    <td style={{ width: "200px", padding: "4px 8px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          style={{
                            width: "125px",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginRight: "5px",
                          }}
                          maxLength={200}
                          value={noteDrafts[order.orderId] || ""}
                          onChange={(e) =>
                            handleNoteChange(order.orderId, e.target.value)
                          }
                          title={
                            noteDrafts[order.orderId] ||
                            order.internalNotes ||
                            "-"
                          }
                        />
                        <button
                          className="btn btn-sm btn-success"
                          style={{ fontSize: "12px", padding: "4px 8px" }}
                          onClick={() => handleNoteSave(order.orderId)}
                          disabled={savingNoteId === order.orderId}
                        >
                          Save
                        </button>
                      </div>
                    </td>
                    <td
                      style={{
                        maxWidth: "100px",
                        backgroundColor:
                          order.tagIds && order.tagIds.length > 0
                            ? "#006400"
                            : "inherit",
                        color:
                          order.tagIds && order.tagIds.length > 0
                            ? "white"
                            : "inherit",
                      }}
                    >
                      <select
                        className="form-select form-select-sm"
                        value={order.tagIds?.[0] ?? "remove"}
                        onChange={(e) =>
                          handleTagChange(order.orderId, e.target.value)
                        }
                        style={{
                          backgroundColor:
                            order.tagIds && order.tagIds.length > 0
                              ? "#006400"
                              : "white",
                          color:
                            order.tagIds && order.tagIds.length > 0
                              ? "white"
                              : "black",
                        }}
                      >
                        <option value="remove">No tag</option>
                        {tags.map((tag) => (
                          <option key={tag.tagId} value={tag.tagId}>
                            {tag.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td
                      style={{
                        maxWidth: "120px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                      title={firstItem.sku || "-"}
                      onClick={() => {
                        navigator.clipboard.writeText(firstItem.sku || "-");
                        toast("SKU copied!", "success");
                      }}
                    >
                      {firstItem.sku || "-"}
                    </td>

                    <td style={{ maxWidth: "20px", textAlign: "center" }}>
                      {totalQuantity}
                    </td>
                    <td>{order.gift ? "🎁" : "-"}</td>
                    <td
                      style={{
                        maxWidth: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                      title={firstItem.name || "-"}
                      onClick={() => {
                        navigator.clipboard.writeText(firstItem.name || "-");
                        toast("Item name copied!", "success");
                      }}
                    >
                      {firstItem.name || "-"}
                    </td>
                    <td
                      style={getRequestedServiceStyle(
                        order.requestedShippingService
                      )}
                    >
                      {order.requestedShippingService || "-"}
                    </td>
                    <td>
                      {new Date(order.createDate).toLocaleDateString("en-US")}
                    </td>
                    <td
                      style={{
                        maxWidth: "120px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                      title={firstItem.sku || "-"}
                      onClick={() => {
                        navigator.clipboard.writeText(firstItem.sku || "-");
                        toast("SKU copied!", "success");
                      }}
                    >
                      {firstItem.sku || "-"}
                    </td>
                    <td
                      style={{
                        backgroundColor:
                          order.orderStatus === "shipped"
                            ? "#d4edda"
                            : order.orderStatus === "cancelled"
                            ? "#f8d7da"
                            : order.orderStatus === "on_hold"
                            ? "#fff3cd"
                            : "inherit",
                        color:
                          order.orderStatus === "shipped"
                            ? "#155724"
                            : order.orderStatus === "cancelled"
                            ? "#721c24"
                            : order.orderStatus === "on_hold"
                            ? "#856404"
                            : "inherit",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {order.orderStatus || "-"}
                    </td>
                    {showEmbroideryColumn && (
                      <td>
                        {order?.tagIds
                          ?.map(Number)
                          .includes(embroideryTagId) ? (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => openEmbroideryModal(order)}
                            disabled={tagsLoading}
                            title={tagsLoading ? "Loading tags..." : "Edit"}
                          >
                            {tagsLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Loading
                              </>
                            ) : (
                              "Edit"
                            )}
                          </button>
                        ) : null}
                      </td>
                    )}

                    <td
                      style={{
                        backgroundColor:
                          order.is_placed === true
                            ? "#198754"
                            : order.design_uploaded === true
                            ? "#198754"
                            : "inherit",
                        color:
                          order.is_placed === true ||
                          order.design_uploaded === true
                            ? "white"
                            : "inherit",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {order.is_placed === true
                        ? "Placed"
                        : order.design_uploaded === true
                        ? "Uploaded"
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* PAGINATION COMPONENT */}
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            totalOrders={totalOrdersCount}
            onPageChange={(newPage) => {
              setCurrentPage(newPage);
              fetchOrders(
                selectedShop.id,
                selectedShop.name,
                statusFilter,
                newPage
              );
            }}
          />
        </div>
      )}

      {showEmbModal && selectedEmbOrder && (
        <EmbroideryUploadModal
          show={showEmbModal}
          onClose={() => setShowEmbModal(false)}
          order={selectedEmbOrder}
        />
      )}
    </div>
  );
};

export default MyOrdersPage;
